<!--
  line-item-checkoff.component.html

  One collapsible block per line: a header that always says what happened to the
  line, and a body with the controls. Lifted from the mobile Items step so the
  receiving-log edit screen renders the same control the delivery was logged with.

  The header carries every pill — canceled, closed, arrival status, damage — because
  a collapsed row has to be readable without opening it. The damage pill sits
  ALONGSIDE the arrival pill rather than replacing it: a refused delivery still
  arrived, and the tap that recorded it is a real event.

  Damage EDITING is not here. The host supplies it through `linePanel`, rendered at
  the bottom of the body — see the component's class doc for why.
-->

<!--
  `trackBy` is load-bearing, not an optimisation. Every edit handler replaces the
  row object (`patch` emits `{ ...row, ...change }` in a new array), so under
  Angular's default identity tracking the row at that index is an object it has
  never seen and it destroys and rebuilds the whole row's DOM — taking the focused
  quantity input with it, once per keystroke. Keyed on the line's own identity
  instead, the replaced object re-renders in place and the caret stays put.
-->
<div class="lic-row" *ngFor="let row of rows; let i = index; trackBy: trackRow"
     [class.lic-row-complete]="row.status === 'complete'"
     [class.lic-row-partial]="row.status === 'partial'"
     [class.lic-row-closed]="isClosed(row)"
     [class.lic-row-canceled]="isCanceled(row)">

  <button type="button" class="lic-head" (click)="toggle(i)"
          [attr.aria-expanded]="isExpanded(i)"
          [disabled]="isCanceled(row)">
    <span class="lic-num">{{ row.isOffPo ? "ADD'L" : 'L' + row.line }}</span>
    <span class="lic-desc">
      <span *ngIf="row.itemNumber" class="lic-itemno">{{ row.itemNumber }}</span>
      {{ row.description }}
    </span>

    <span *ngIf="isCanceled(row)" class="lic-pill-canceled">Canceled</span>
    <!-- A legacy PO's tracked total is not the whole truth (earlier deliveries are
         not in the ledger), so the pill says the number is a TRACKED number. -->
    <span *ngIf="!isCanceled(row) && isClosed(row)" class="lic-pill-closed">
      {{ isLegacyPo ? 'Received (tracked)' : 'Closed' }}
    </span>
    <span *ngIf="row.status" class="lic-pill" [ngClass]="'lic-pill-' + row.status">
      {{ statusLabel(row) }}
    </span>
    <span *ngIf="damageBadge(row)" class="lic-pill-damage"
          [class.lic-pill-damage-missing]="isRefusedOrReturned(row)">
      {{ damageBadge(row) }}
    </span>

    <!-- Quantity and the tap hint only on an open, unmarked line: a closed line's
         pill already says it is received, and its counts show in the body. -->
    <span *ngIf="!isReadOnly(row) && !isClosed(row) && !row.status && qtyDisplay(row)"
          class="lic-qty">{{ qtyDisplay(row) }}</span>
    <span *ngIf="!isReadOnly(row) && !isClosed(row) && !row.status && !isExpanded(i)"
          class="lic-tap-hint">Select to mark</span>

    <ion-icon *ngIf="!isCanceled(row)" class="lic-chevron"
              [class.lic-chevron-open]="isExpanded(i)" name="chevron-down-outline"></ion-icon>
  </button>

  <div class="lic-body" *ngIf="isExpanded(i) && !isCanceled(row)">

    <ng-container *ngIf="!row.isOffPo">
      <!-- The individual receipt rows behind the line's tracked total, read-only.
           On a ragged line these are the only place its progress shows. -->
      <div class="lic-receipts" *ngIf="priorReceipts(row).length > 0">
        <div class="lic-receipts-label">
          {{ isRagged(row) ? 'Parts received so far' : 'Already received' }}
        </div>
        <div class="lic-receipt" *ngFor="let receipt of priorReceipts(row)">
          <ion-icon name="checkmark-circle-outline"></ion-icon>
          <span>{{ receiptLabel(receipt) }}</span>
        </div>
      </div>

      <!-- Read-only, with the host's reason. A locked row that just refuses to
           respond is indistinguishable from a broken one. -->
      <div class="lic-closed-note" *ngIf="isReadOnly(row)">
        <ion-icon name="checkmark-circle-outline"></ion-icon>
        {{ readOnlyNote(row) || 'Fully received — no action needed.' }}
      </div>

      <ng-container *ngIf="!isReadOnly(row)">
        <div class="lic-actions">
          <button type="button" class="lic-btn lic-btn-complete"
                  [class.lic-active]="row.status === 'complete'"
                  (click)="setStatus(i, 'complete')">
            <ion-icon name="checkmark-outline"></ion-icon>
            Arrived
          </button>
          <button type="button" class="lic-btn lic-btn-partial"
                  [class.lic-active]="row.status === 'partial'"
                  (click)="setStatus(i, 'partial')">
            <ion-icon name="git-branch-outline"></ion-icon>
            Partial
          </button>
          <!-- No "Missing" action anywhere: what is still owed depends on what
               arrived, so it is never a per-line tap here. -->
          <button type="button" class="lic-btn" *ngIf="allowClearStatus && row.status"
                  (click)="clearStatus(i)">
            <ion-icon name="close-outline"></ion-icon>
            Not on this delivery
          </button>
        </div>

        <!-- Collapses an opened-but-unmarked line. Only while unmarked: once a
             choice is made the row stays open for review and closes by its chevron,
             so an open row never looks handled when nothing was marked.

             A quiet text button, NOT a `lic-btn` like the two above it — it is a
             secondary "never mind", and giving it the status treatment makes it read
             as a third choice. It is also not destructive: nothing is discarded, so
             it must not carry danger styling either. -->
        <button type="button" class="lic-cancel-btn" *ngIf="!row.status" (click)="collapse(i)">
          Cancel
        </button>

        <div class="lic-closed-note" *ngIf="errorAtIndex === i && errorText">
          <ion-icon name="alert-circle-outline"></ion-icon>
          {{ errorText }}
        </div>

        <!-- ONE editable box — what arrived in THIS delivery — against a read-only
             remaining, so the count cannot land in the wrong one (which would leave
             the missing math unable to subtract it). -->
        <div class="lic-qty-row" *ngIf="row.status === 'partial'">
          <div class="lic-qty-field">
            <div class="lic-qty-label">Arrived now</div>
            <!-- type="text", NOT "number": Angular's number value accessor would hand
                 the change handler a number (or null when emptied), and every quantity
                 on the row is a STRING by contract because a blank and a typed zero
                 mean different things. inputmode still gives the numeric keypad. -->
            <input class="lic-qty-input" type="text" inputmode="numeric" placeholder="0"
                   [ngModel]="row.receivedQty" (ngModelChange)="setReceivedQty(i, $event)"
                   [attr.aria-label]="'Quantity arrived now, line ' + row.line" />
          </div>
          <span class="lic-qty-sep">of</span>
          <div class="lic-qty-field">
            <div class="lic-qty-label">Remaining</div>
            <div class="lic-qty-static">{{ row.totalQty || row.quantity || '—' }}</div>
          </div>
        </div>

        <!-- Ragged shipment (§4): the specific parts, for when a count does not fit.
             Optional — fill whichever suits, quantity or parts. -->
        <div class="lic-parts-row" *ngIf="row.status === 'partial'">
          <div class="lic-qty-label">Or list parts received</div>
          <input class="lic-parts-input" type="text"
                 placeholder="e.g. 2 elbows, 1 tee — the rest still to come"
                 [ngModel]="row.partsReceived" (ngModelChange)="setParts(i, $event)"
                 [attr.aria-label]="'Parts received, line ' + row.line" />
        </div>

        <!-- Completing a ragged line is a "line done" close: the count cannot be
             inferred, so nothing has to be listed. Offered, never required. -->
        <div class="lic-ragged-note" *ngIf="row.status === 'complete' && isRagged(row)">
          <div class="lic-qty-label">
            Marks the remaining parts as received — no need to list them.
          </div>
          <input class="lic-parts-input" type="text" placeholder="List what arrived (optional)"
                 [ngModel]="row.partsReceived" (ngModelChange)="setParts(i, $event)"
                 [attr.aria-label]="'Parts arrived, line ' + row.line" />
        </div>

        <!-- NO "nothing more is coming" tick. Whether the line is closed is DERIVED
             from the choice above it — Arrived closes it, Partial leaves it as it
             was, refused/returned goods reopen it, and clearing the status takes its
             receipt rows off so it reopens on its own. See the component's class doc.
             A checkbox here would be a second opinion about the same fact. -->
      </ng-container>
    </ng-container>

    <!-- Off-PO rows are fully received by definition of having been added, so there
         is no Arrived/Partial choice — only the amount. Host-gated: the submit edits
         an off-PO item through its own form in `linePanel`, since the description was
         typed there too. -->
    <ng-container *ngIf="row.isOffPo && allowOffPoQtyEdit">
      <div class="lic-qty-row">
        <div class="lic-qty-field">
          <div class="lic-qty-label">Arrived</div>
          <!-- type="text" for the same reason as the partial quantity above. -->
          <input class="lic-qty-input" type="text" inputmode="numeric"
                 [ngModel]="row.quantity" (ngModelChange)="setOffPoQty(i, $event)"
                 [attr.aria-label]="'Quantity received of ' + row.description" />
        </div>
        <span class="lic-qty-sep">{{ row.unit }}</span>
      </div>
      <!-- Removing an off-PO row DELETES its receipt from the ledger, so it is
           host-gated rather than always offered: mobile has its own Remove, which also
           has to clear any damage flagged on the row.

           Styled as its own destructive action rather than reusing `lic-cancel-btn`.
           That class is the quiet "never mind" under an unmarked line — correct there,
           because nothing is discarded — and wearing it here made the one control on
           this row that DELETES a ledger row the least visible thing on it. Only the
           edit screen renders this (`allowOffPoRemove` is off for the submit), so the
           louder treatment cannot leak into mobile. -->
      <button type="button" class="lic-remove-btn" *ngIf="allowOffPoRemove"
              (click)="removeOffPoRow(i)">
        <ion-icon name="trash-outline"></ion-icon>
        Remove this item
      </button>
    </ng-container>

    <!-- The host's per-row extras — the mobile step's damage panel. -->
    <ng-container *ngIf="linePanel"
                  [ngTemplateOutlet]="linePanel"
                  [ngTemplateOutletContext]="{ $implicit: row, index: i }">
    </ng-container>
  </div>
</div>
